/**
 * Standardized data grid — TanStack Table + MUI chrome with full SOTA viewport ladder:
 *   xs–sm  → card list (auto or custom)
 *   md     → compact table (hideBelow columns collapsed)
 *   lg–xl  → full table
 */
import React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { PaygoBreakpoint } from "../theme/breakpoints";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    technical?: boolean;
    /** Hide this column below the given breakpoint (still available via technical toggle if set). */
    hideBelow?: PaygoBreakpoint;
  }
}

export function AppDataTable<TData>({
  data,
  columns,
  onRowClick,
  enableSearch = true,
  pageSize = 10,
  renderMobileCard,
  cardBelow = "md",
}: {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  onRowClick?: (row: TData) => void;
  enableSearch?: boolean;
  pageSize?: number;
  /** Custom card for xs–sm. If omitted, an auto card is built from visible columns. */
  renderMobileCard?: (row: TData) => React.ReactNode;
  /** Show cards below this breakpoint (default md = tablet). */
  cardBelow?: PaygoBreakpoint;
}) {
  const theme = useTheme();
  const useCards = useMediaQuery(theme.breakpoints.down(cardBelow));
  const upSm = useMediaQuery(theme.breakpoints.up("sm"));
  const upMd = useMediaQuery(theme.breakpoints.up("md"));
  const upLg = useMediaQuery(theme.breakpoints.up("lg"));

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const c of columns) {
      const id =
        (c as { id?: string; accessorKey?: string }).id ??
        (c as { accessorKey?: string }).accessorKey;
      if (!id) continue;
      if (c.meta?.technical) {
        next[id] = false;
        continue;
      }
      const hideBelow = c.meta?.hideBelow;
      if (!hideBelow) {
        next[id] = true;
        continue;
      }
      // Progressive disclosure: show column only once viewport reaches hideBelow
      if (hideBelow === "sm") next[id] = upSm;
      else if (hideBelow === "md") next[id] = upMd;
      else if (hideBelow === "lg") next[id] = upLg;
      else next[id] = true;
    }
    setColumnVisibility(next);
  }, [columns, upSm, upMd, upLg]);

  const table = useReactTable({
    data,
    columns,
    state: { columnVisibility, sorting, globalFilter },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: { pageSize },
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const fv = String(filterValue ?? "").toLowerCase().trim();
      if (!fv) return true;
      return row.getAllCells().some((cell) => {
        const value = cell.getValue();
        return String(value ?? "").toLowerCase().includes(fv);
      });
    },
  });

  const { pageIndex, pageSize: size } = table.getState().pagination;
  const technicalCols = table.getAllLeafColumns().filter((c) => c.columnDef.meta?.technical);
  const pageRows = table.getRowModel().rows;

  const toolbar = (
    <Toolbar
      sx={{
        gap: 2,
        flexWrap: "wrap",
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1.5, sm: 2 },
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {enableSearch ? (
        <TextField
          size="small"
          placeholder="Search…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 220 }, flex: { sm: "1 1 200px" } }}
        />
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
      {!useCards && technicalCols.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {technicalCols.map((col) => (
            <FormControlLabel
              key={col.id}
              control={
                <Checkbox
                  size="small"
                  checked={col.getIsVisible()}
                  onChange={col.getToggleVisibilityHandler()}
                />
              }
              label={
                <Typography variant="caption">
                  {typeof col.columnDef.header === "string" ? col.columnDef.header : col.id}
                </Typography>
              }
            />
          ))}
        </Box>
      ) : null}
    </Toolbar>
  );

  const pagination = (
    <TablePagination
      component="div"
      count={table.getFilteredRowModel().rows.length}
      page={pageIndex}
      onPageChange={(_e, page) => table.setPageIndex(page)}
      rowsPerPage={size}
      onRowsPerPageChange={(e) => {
        table.setPageSize(Number(e.target.value));
        table.setPageIndex(0);
      }}
      rowsPerPageOptions={[5, 10, 25, 50]}
      sx={{
        ".MuiTablePagination-toolbar": {
          flexWrap: "wrap",
          minHeight: { xs: 56, sm: 52 },
          px: { xs: 1, sm: 2 },
        },
        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
          mb: { xs: 0.5, sm: 0 },
        },
      }}
    />
  );

  if (useCards) {
    return (
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {toolbar}
        <Stack spacing={1.25} sx={{ p: { xs: 1.5, sm: 2 } }}>
          {pageRows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No rows to display
            </Typography>
          ) : (
            pageRows.map((row) => {
              if (renderMobileCard) {
                return (
                  <Box
                    key={row.id}
                    onClick={() => onRowClick?.(row.original)}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {renderMobileCard(row.original)}
                  </Box>
                );
              }
              const visible = row.getVisibleCells().filter((c) => !c.column.columnDef.meta?.technical);
              const primary = visible[0];
              const rest = visible.slice(1, 4);
              return (
                <Card key={row.id} variant="outlined">
                  <CardActionArea
                    disabled={!onRowClick}
                    onClick={() => onRowClick?.(row.original)}
                    sx={{ minHeight: 64, alignItems: "stretch" }}
                  >
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, mb: rest.length ? 0.75 : 0 }}>
                        {primary
                          ? flexRender(primary.column.columnDef.cell, primary.getContext())
                          : "—"}
                      </Typography>
                      <Stack spacing={0.5}>
                        {rest.map((cell) => (
                          <Stack
                            key={cell.id}
                            direction="row"
                            spacing={1}
                            sx={{ justifyContent: "space-between", gap: 1 }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {typeof cell.column.columnDef.header === "string"
                                ? cell.column.columnDef.header
                                : cell.column.id}
                            </Typography>
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{ textAlign: "right", wordBreak: "break-word" }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })
          )}
        </Stack>
        {pagination}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {toolbar}
      <TableContainer sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableCell
                      key={header.id}
                      sortDirection={sorted === false ? false : sorted}
                      sx={{
                        bgcolor: "background.default",
                        fontWeight: 600,
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        px: { md: 1.5, lg: 2 },
                      }}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <TableSortLabel
                          active={!!sorted}
                          direction={sorted === "desc" ? "desc" : "asc"}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No rows to display
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover={!!onRowClick}
                  onClick={() => onRowClick?.(row.original)}
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} sx={{ px: { md: 1.5, lg: 2 } }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </Paper>
  );
}
